import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { getMetricValue } from './claimsTrendMetricsCardHelper';
import { useAllMetrics } from 'features/metrics/useAllMetrics';
import { IconInfoCircle } from '@tabler/icons-react';
import CustomTooltip from 'common/customTooltip';
import { MetricCardErrorState, MetricCardLoadingState, MetricCardNoDataText } from './MetricCardStates';

const SurplusDeficitMetricsCard = ({ width, height }) => {
  const [toggle, setToggle] = useState('Dollar');

  // Use unified all-metrics VDS hook (single call for all 8 tiles)
  const { data: metricsData, loading, error } = useAllMetrics();

  const handleToggle = (event, newValue) => {
    if (newValue) setToggle(newValue);
  };

  let measureName = null;
    if(toggle === 'Dollar') {
      measureName = 'Surplus Deficit Curr';
    } else {
      measureName = 'Surplus Deficit %';
    }

    // Safely extract the value
    let extractedValue = null;
    extractedValue = getMetricValue(metricsData, measureName);

    const formatDollarValue = (value) => {
      if (value === null || value === undefined) return null;
      
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return null;
      
      const absValue = Math.abs(numValue);
      const isNegative = numValue < 0;
      
      let formattedValue;
      
      if (absValue >= 1000000) {
        // Millions: $13,816,123 -> $13.82M (or $14M for whole numbers)
        const millions = absValue / 1000000;
        const formattedNum = millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(2);
        formattedValue = `$${formattedNum}M`;
      } else if (absValue >= 10000) {
        // Thousands: $50,000 -> $50K (or $13.82K for decimals)
        const thousands = absValue / 1000;
        const formattedNum = thousands % 1 === 0 ? thousands.toFixed(0) : thousands.toFixed(2);
        formattedValue = `$${formattedNum}K`;
      } else {
        // Less than 10K: show full number with commas
        formattedValue = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(absValue);
      }
      
      return isNegative ? `-${formattedValue}` : formattedValue;
    };

    const tooltipIcon = (
    <CustomTooltip
      title={'Year-to-date difference between expected and actual plan spend. View in dollars or as a percentage of YTD premium.'}
      arrow
      placement="bottom-start"
      padding="16px"
      borderRadius="8px 0px 8px 8px"
      width='308px'
      popperProps={{
        modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: ({ placement }) =>
                      placement === 'bottom-end' ? [-9, -3] : [9, -3]
                  }
                },
                {
                  name: 'flip',
                  options: {
                    fallbackPlacements: ['bottom-end'],
                    padding: 8,
                  }
                }
              ]
      }}>
      <IconButton
        size="small"
        sx={{
          p: 0,
          cursor: 'pointer',
        }}
        aria-label="Surplus/Deficit info"
      >
        <IconInfoCircle size={24} aspectRatio="1/1" color="#000" />
      </IconButton>
    </CustomTooltip>
  );

  // Show loading skeleton while data is being fetched
  if (loading) {
    return <MetricCardLoadingState width={width} height={height} borderRadius="26px" />;
  }

  if (error) {
    return <MetricCardErrorState title="Surplus/Deficit (YTD)" titleSx={{ whiteSpace: 'nowrap' }} headerRight={tooltipIcon} />;
  }
  if(error || !metricsData || extractedValue === null || extractedValue === undefined) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        height: '176px',
        padding: '24px',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '8px',
        alignSelf: 'stretch',
        borderRadius: '16px',
        border: '1px solid var(--Gray---Line-Stroke, #71767A)',
        background: 'var(--white-bi, #FFF)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', alignSelf: 'stretch' }}>
        <Typography 
          sx={{ 
            color: 'var(--Gray-Darkest---Body-Text, #121A22)', 
            fontFamily: 'Noto Sans',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: '150%',
            whiteSpace: 'nowrap'
          }}
        >
          Surplus/Deficit (YTD)
        </Typography>
        {tooltipIcon}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          alignSelf: 'stretch'
        }}
      >
      <ToggleButtonGroup
        value={toggle}
        exclusive
        onChange={handleToggle}
        size="small"
        sx={{
          '& .MuiToggleButton-root': {
            border: 'none',
            borderRadius: '8px',
            bgcolor: 'transparent',
            px: 0,
            py: 0,
            fontSize: 14,
            fontWeight: 400,
            fontFamily: 'Noto Sans',
            lineHeight: '150%',
            color: '#2F54A5',
            textTransform: 'none',
            '&:hover': { bgcolor: 'transparent' },
            '&:hover:not(.Mui-selected)': { color: '#068DF1' },
            '&.Mui-selected:hover': { bgcolor: 'transparent' },
            '&.Mui-selected': {
              bgcolor: 'transparent',
              fontWeight: 600,
              textDecoration: 'underline',
              textDecorationSkipInk: 'none',
              color: '#2F54A5'
            }
          },
          '& .MuiToggleButton-root + .MuiToggleButton-root': {
            marginLeft: '12px'
          }
        }}
      >
        <ToggleButton value="Dollar">Dollar</ToggleButton>
        <ToggleButton value="Percentage">Percentage</ToggleButton>
      </ToggleButtonGroup>
      </Box>
      {extractedValue != null  ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', alignSelf: 'stretch', marginTop: 'auto' }}>
      <Typography
          sx={{
            fontSize: '20px',
            fontWeight: 400,
            fontStyle: 'normal',
            fontFamily: 'Noto Sans',
            color: '#121A22',
            lineHeight: '150%',
          }}
      >
          <>
            {toggle === 'Dollar' ? (
              formatDollarValue(extractedValue)
            ) : (
              `${parseFloat(extractedValue).toFixed(2)}%`
            )}
          </>
      </Typography>
      </Box>
       ) : (
        <MetricCardNoDataText />
      )}
    </Box>
  );
};

export default SurplusDeficitMetricsCard;